// react
import { useCallback, useMemo } from 'react';

// auth
import { useAuth } from './useAuth';

// store
import { dispatch, useSelector } from 'store';
import {
  switchCareCircle as switchCareCircleAction,
  getCurrentCareCircle,
  getAccountCareCircles,
  getCurrentCareCircleMember,
} from 'store/slices/careCircleSlice';
import { useGetCareCircleQuery } from 'store/api/careCircle';

// models
import { CareCircleMember } from '@karehero/models';

export const useCareCircle = () => {
  // hooks
  const { isAuthenticated, isLoading } = useAuth();

  const currentCareCircle = useSelector(getCurrentCareCircle);
  const careCircleId = currentCareCircle?.id;

  const { data: careCircle } = useGetCareCircleQuery(careCircleId, {
    skip: !careCircleId || isLoading || !isAuthenticated,
  });

  const accountCareCircles = useSelector(getAccountCareCircles);
  const careCircleMember: CareCircleMember = useSelector(
    getCurrentCareCircleMember,
  );

  // memo
  const primaryCareCircleMember = useMemo(
    () =>
      careCircle &&
      careCircle.careCircleMembers?.find(
        (m) => m.account.id === careCircle?.primaryCaregiverAccount.id,
      ),
    [careCircle],
  );

  const careRecipientFirstName = useMemo(
    () => careCircle?.careRecipientAccount?.firstName,
    [careCircle],
  );

  const careRecipientName = useMemo(() => {
    let name = `${careCircle?.careRecipientAccount?.firstName || ''}`;
    if (careCircle?.careRecipientAccount?.lastName) {
      name += ` ${careCircle?.careRecipientAccount?.lastName}`;
    }
    return name;
  }, [careCircle]);

  const switchCareCircle = useCallback(
    async (careCircleId: string) => {
      await dispatch(switchCareCircleAction(careCircleId));
    },
    [dispatch, switchCareCircleAction],
  );

  return {
    switchCareCircle,
    careCircle,
    careCircleMember,
    primaryCareCircleMember,
    accountCareCircles,
    careCircleId,
    careRecipientFirstName,
    careRecipientName,
  };
};
